import React, { useState } from "react";
import {
  ViewMe,
  useAddIdentity,
  useGetCBM,
  useMyIdentities,
} from "@njoy/controller";
import { ConnectIdSchema } from "@njoy/common";
import { useNavigate } from "react-router-dom";
import { Form as AntForm } from "antd";
import { Form, Formik, FormikHelpers as FormikActions } from "formik";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tooltip } from "antd";
import NewButton from "../../../components/uiMerge/newButton";
import { TemplateSub } from "../../../components/layouts/TemplateSub";
import { Page1 } from "./ui/Page1";
import Loader from "../../../components/loader/Loader";
import { VerificationFailedModal } from "../../../components/modals/verificationFailedModal";
import ToastMessage from "../../../hooks/useToast";
import { extractError } from "../../../utils/extractError";
import { VerifyNinModal } from "../../../components/modals/verifyNinModal";
import * as ROUTES from "../../../constants/routes";
import Button from "../../../components/uiMerge/newButton";

const FormItem = AntForm.Item;

export interface FormValues {
  communityId: string;
  id: string;
  communityEntityId: string;
  idNumber: string;
  isStudentCommunity: boolean;
  isNyscCommunity: boolean;
  isCbmCommunity: boolean;
  requiresCommunityEntity: boolean;
}

export const initialValues: FormValues = {
  communityId: "",
  id: "",
  communityEntityId: "",
  idNumber: "",
  isStudentCommunity: false,
  isNyscCommunity: false,
  isCbmCommunity: false,
  requiresCommunityEntity: false,
};

export const AddIdentityConnector: React.FC = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(false);
  const [errorCount, setErrorCount] = React.useState(0);
  const [supportIssue, setSupportIssue] = React.useState<{
    message: string;
    errorCode?: string;
    supportRef?: string;
  } | null>(null);
  const [supportExtraContext, setSupportExtraContext] = React.useState<
    Record<string, string | number | boolean | null | undefined>
  >({});
  const [operationName, setOperationName] = React.useState(
    "AddIdentityMutation",
  );
  const { addIdentity } = useAddIdentity();
  const { getCBM } = useGetCBM();
  const { myIdentities } = useMyIdentities({ limit: 5, offset: 0 });
  const showModal = () => setVisible(true);
  const handleCancel = () => setVisible(false);

  // const onFinish = (result: any) => {
  //   const message = result?.data?.addIdentity?.message;
  //   if (
  //     message &&
  //     message.trim().toLowerCase() === "identity added successfully!"
  //   ) {
  //     navigate(ROUTES.CONNECT_ID_SUCCESS);
  //   } else {
  //     showModal();
  //   }
  // };

  const submit = async (
    values: FormValues,
    { setSubmitting, setErrors, resetForm }: FormikActions<FormValues>,
  ) => {
    setSubmitting(true);
    try {
      const { communityId, communityEntityId, idNumber, isCbmCommunity } =
        values;
      const submissionValues = communityEntityId
        ? { communityId, communityEntityId, idNumber }
        : { communityId, idNumber };
      setOperationName(
        isCbmCommunity ? "GetCbmMutation" : "AddIdentityMutation",
      );
      const result = isCbmCommunity
        ? await getCBM({ memberId: idNumber.trim() })
        : await addIdentity(submissionValues);
      setSubmitting(false);

      if (!result.errors || Object.keys(result.errors).length === 0) {
        setSupportIssue(null);
        setErrorCount(0);
        ToastMessage(
          "success",
          isCbmCommunity
            ? "CBM ID verified successfully!"
            : result.message || "Identity added successfully!",
        );
        resetForm();
        navigate(ROUTES.CONNECT_ID_SUCCESS);
        return;
      }

      const errorSummary = extractError(result.errors);
      const supportMeta = (result as {
        supportMeta?: {
          code?: string;
          supportRef?: string;
        };
      }).supportMeta;

      setSupportIssue({
        message: errorSummary,
        errorCode: supportMeta?.code,
        supportRef: supportMeta?.supportRef,
      });
      setSupportExtraContext({
        communityId,
        communityEntityId: communityEntityId || "",
        idNumberLength: idNumber?.length || 0,
        hasCommunityEntity: Boolean(communityEntityId),
        isCbmCommunity,
      });
      setErrorCount((currentCount) => currentCount + 1);

      ToastMessage("error", errorSummary);
      setErrors(result.errors);
      showModal();
    } catch (err) {
      setSubmitting(false);
      setSupportIssue({
        message: "Something went wrong. Please try again.",
      });
      setSupportExtraContext({
        exceptionMessage: (err as Error)?.message || "",
      });
      setErrorCount((currentCount) => currentCount + 1);
      ToastMessage("error", "Something went wrong. Please try again.");
      showModal();
    }
  };

  const toolTipText =
    myIdentities.length > 2
      ? "You can only add up to 3 identities."
      : "Verify your NIN before adding an additional ID.";
  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ViewMe>
      {(data) => {
        if (!data.match) {
          return <Loader center text="Loading..." noLeftMargin />;
        }
        const isVerifyDisabled =
          !data.match?.customer?.nin ||
          data.match.customer.nin === "" ||
          myIdentities.length > 2;

        return (
          <>
            {/* Show VerifyNinModal only if NIN does not exist */}
            {(!data.match?.customer?.nin || data.match.customer.nin === "") && (
              <VerifyNinModal isOpen={open} onClose={handleClose} />
            )}

            <ToastContainer />

            <Formik<FormValues>
              initialValues={initialValues}
              validationSchema={ConnectIdSchema}
              onSubmit={submit}
            >
              {({ isSubmitting }) => (
                <TemplateSub page={"Connect ID"} showNavBar={false}>
                  <div className="flex flex-col h-full flex-1">
                    <div className="mt-10 flex flex-col gap-2 px-[1rem]">
                      <p className="text-[20px] text-white font-bold">
                        Select Additional ID
                      </p>
                      <p className="text-[16px] text-white opacity-60">
                        You can add other valid IDs to unlock more perks and
                        benefits.
                      </p>
                    </div>

                    <Form className="flex flex-col h-full flex-1 mt-[1rem]">
                      <Page1 />
                      <div className="mt-auto px-[1rem] flex flex-row space-x-2">
                        <div className="flex-1">
                          <Button
                            variant="outline"
                            onClick={() => navigate(ROUTES.HOME)}
                            className="w-full mb-4 bg-transparent border cursor-pointer border-white text-white"
                          >
                            Skip
                          </Button>
                        </div>
                        <FormItem className="flex-1">
                          <Tooltip
                            title={isVerifyDisabled ? toolTipText : null}
                            placement="top"
                          >
                            <NewButton
                              disabled={isVerifyDisabled}
                              fullWidth
                              loading={isSubmitting}
                              variant="primary"
                              type="submit"
                            >
                              Verify
                            </NewButton>
                          </Tooltip>
                        </FormItem>
                      </div>
                    </Form>
                  </div>
                </TemplateSub>
              )}
            </Formik>

            <VerificationFailedModal
              visible={visible}
              onCancel={handleCancel}
              onOk={handleCancel}
              supportIssue={supportIssue}
              errorCount={errorCount}
              operationName={operationName}
              extraContext={supportExtraContext}
            />
          </>
        );
      }}
    </ViewMe>
  );
};
